/**
 * useCupoPorEntrada — admin venta selector (taquilla, sin recinto).
 *
 * Encapsulates the "shared cup across entradas" invariant for the admin
 * selector. Given a list of entradas with user-selected `unidades` and the
 * shared `cupoCompartido`, returns a function `cap(entrada)` that computes
 * the maximum selectable units for that entrada, considering the sum of
 * units already chosen in OTHER entradas.
 *
 * Behaviour:
 *   - Legacy branch (cupoCompartido null/undefined): preserved byte-for-byte.
 *     `cap = min(disponibles, n_entradas_max ?? 8)`.
 *   - Stock compartido branch:
 *     `cap = min(disponibles, max(0, cup - sumaOtras), n_entradas_max?)`.
 *     `sumaOtras` is the sum of `unidades` selected in every entrada except
 *     the one being asked about, so the total across all entradas cannot
 *     exceed the shared cup. `n_entradas_max` is an INDEPENDENT, ACCUMULATIVE
 *     per-ticket-type purchase cap that stays enforced when stock_compartido
 *     is active — activating shared stock does NOT relax the existing per-row
 *     limit. The per-row max is derived with the SAME legacy truthiness rule
 *     as the non-stock branch (falsy field — null/undefined/''/0/NaN/false
 *     — falls back to 8) so that payloads which pre-date the stock_compartido
 *     branch keep their previous cap instead of being relaxed. Admin-only
 *     defensive clamp `> 100 → 100` is preserved (front does not apply it).
 *   - Preventa (n_max_asientos) narrows the cap orthogonally when active.
 *   - FAILSAFE: stock compartido with non-finite/negative cup → returns 0
 *     and emits a debounced console.error (per instance).
 *
 * @param {import('vue').Ref<Array>} entradas
 * @param {import('vue').ComputedRef<number|null>|import('vue').Ref<number|null>} cupoCompartido
 * @param {import('vue').ComputedRef<number|null>|import('vue').Ref<number|null>} [preventaMax]
 * @returns {(entrada: object) => number}
 */
export function useCupoPorEntrada(entradas, cupoCompartido, preventaMax) {
    let hasLoggedDrift = false;

    return entrada => {
        // Branch off → legacy byte-for-byte (incluye clamp defensivo max > 100).
        if (cupoCompartido.value === null || cupoCompartido.value === undefined) {
            let max = entrada.n_entradas_max ? entrada.n_entradas_max : 8;

            if (max > 100) {
                max = 100;
            }

            return entrada.disponibles > max ? max : entrada.disponibles;
        }

        const cup = Number(cupoCompartido.value);

        if (!Number.isFinite(cup) || cup < 0) {
            if (process.env.NODE_ENV !== 'production' && !hasLoggedDrift) {
                console.error('[useCupoPorEntrada] stock_compartido=true sin cupo_disponible válido — payload drift');
                hasLoggedDrift = true;
            }
            return 0;
        }

        // Sum the units already selected in OTHER entradas (NOT the current one).
        const sumaOtras = entradas.value.reduce((acc, e) => {
            if (e === entrada) {
                return acc;
            }
            return acc + (Number(e.unidades) || 0);
        }, 0);

        const restante = Math.max(0, cup - sumaOtras);
        let base = entrada.disponibles > restante ? restante : entrada.disponibles;

        // n_entradas_max is an independent, accumulative per-row cap that must
        // remain enforced even when stock_compartido is active. Shared stock
        // does not relax the existing per-ticket-type purchase limit — it only
        // adds a SUM constraint on top of it. Derive the legacy max first
        // using the SAME truthiness rule as the non-stock branch
        // (falsy field → fallback to 8). `Number(null)` and `Number('')` are 0
        // (not NaN), so doing the conversion first would clamp the cap to 0
        // and mark the entrada as AGOTADA. `Number(undefined)` is NaN, so
        // doing the conversion first would skip the leg and relax the
        // previous cap from 8 to whatever the cup allows. The truthiness
        // step is the legacy byte-compat anchor.
        const nMaxRaw = entrada.n_entradas_max ? entrada.n_entradas_max : 8;
        let nMax = Number(nMaxRaw);

        if (!Number.isFinite(nMax) || nMax < 0) {
            nMax = 8;
        }

        // Admin legacy defensive clamp: max cannot exceed 100.
        // (Front branch does NOT apply this — match its legacy behaviour.)
        if (nMax > 100) {
            nMax = 100;
        }

        if (base > nMax) {
            base = nMax;
        }

        // Preventa narrows the cap orthogonally.
        if (preventaMax && preventaMax.value !== null && preventaMax.value !== undefined) {
            const preventa = Number(preventaMax.value);

            if (Number.isFinite(preventa) && preventa >= 0 && preventa < base) {
                base = preventa;
            }
        }

        return base;
    };
}